import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, concatMap, forkJoin, Observable, of, timer } from 'rxjs';
import { FilePage, FilePages, MalwareScanningStatus } from '../interfaces';
import { ProjectService } from './project.service';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import { SubsectionService } from './subsection.service';
import { ProjectFileService } from './project-file.service';

export enum TranscodeMonitorType {
  SingleFile,
  Pages,
  SingleFileReplacement,
  PagesReplacement,
}

export type TranscodeMonitorRequest = {
  codec: string;
  fileId: string;
  projectId: string;
  replacementFileId?: string;
  type: TranscodeMonitorType;
};

export type TranscodeMonitorResult = TranscodeMonitorRequest & {
  file?: any;
  isSuccess: boolean;
  pages?: FilePages;
};

type TranscodeMonitorItem = TranscodeMonitorResult & {
  replacementFile?: any;
  updates: BehaviorSubject<TranscodeMonitorResult>;
};

@Injectable({
  providedIn: 'root',
})
export class TranscodeMonitorService {
  private readonly projectService = inject(ProjectService);
  private readonly projectFileService = inject(ProjectFileService);
  private readonly subsectionService = inject(SubsectionService);
  private readonly fileIds = new Set<string>();
  private monitorRecord: Record<string, TranscodeMonitorItem> = {};
  private readonly hasFiles$ = new BehaviorSubject<boolean>(false);
  private readonly replacementIdsKey = 'replacementFileIds';
  timerInterval = 5000;
  stopInterval = 500;
  delayBetweenRequests = 3000;

  constructor() {
    this.setupMonitoring();
  }

  monitorFile(request: TranscodeMonitorRequest): Observable<any> {
    if (!request.fileId) {
      return of(null);
    }

    if (request.fileId && !this.monitorRecord[request.fileId]) {
      this.monitorRecord[request.fileId] = {
        ...request,
        isSuccess: false,
        updates: new BehaviorSubject<TranscodeMonitorResult>(null),
      };
      this.fileIds.add(request.fileId);
      this.updateActiveFile();
    }

    return this.monitorRecord[request.fileId].updates.asObservable();
  }

  monitorReplacementFile(request: TranscodeMonitorRequest): Observable<any> {
    if (!request.fileId) {
      return of(false);
    }

    if (this.isReplacementFileOpened(request.fileId)) {
      return of(true);
    }

    if (request.fileId && !this.monitorRecord[request.fileId]) {
      this.monitorRecord[request.fileId] = {
        ...request,
        isSuccess: false,
        updates: new BehaviorSubject<TranscodeMonitorResult>(null),
      };
      this.fileIds.add(request.fileId);
      this.updateActiveFile();
    }

    return this.monitorRecord[request.fileId].updates.asObservable();
  }

  stopMonitoringFile(fileId: string) {
    if (!this.fileIds.has(fileId)) {
      return;
    }

    this.fileIds.delete(fileId);

    this.monitorRecord[fileId]?.updates.complete();
    delete this.monitorRecord[fileId];
    this.updateActiveFile();
  }

  private updateActiveFile() {
    const hasFiles = this.fileIds.size > 0;
    this.hasFiles$.next(hasFiles);
  }

  private pollFileTranscodeStatus(fileId: string) {
    if (!this.monitorRecord[fileId]) {
      return of(null);
    }

    const record = this.monitorRecord[fileId];
    switch (record.type) {
      case TranscodeMonitorType.Pages:
        if (!record.file?.hasPage) {
          return this.projectFileService.getFileInfo(record.projectId, record.fileId).pipe(
            tap((file) => {
              record.file = file;

              if (file.contentType !== 'application/pdf') {
                const pdfMetadata = file?.transcodeMetadata?.find((c) => c?.codec == 'pdf');
                if (pdfMetadata?.resolution == 'false') {
                  record.updates.next({
                    fileId: record.fileId,
                    projectId: record.projectId,
                    codec: record.codec,
                    type: record.type,
                    file: record.file,
                    isSuccess: false,
                  });

                  this.stopMonitoringWithDelay(fileId);
                  return;
                }
              }
              const currentMetadata = file?.transcodeMetadata?.find((c) => c?.codec == record.codec);
              if (currentMetadata?.resolution == 'false') {
                record.updates.next({
                  fileId: record.fileId,
                  projectId: record.projectId,
                  codec: record.codec,
                  type: record.type,
                  file: record.file,
                  isSuccess: false,
                });

                this.stopMonitoringWithDelay(fileId);
              }
            })
          );
        } else {
          return this.subsectionService.getFilePages(record.fileId).pipe(
            tap((result) => {
              const pagesReady = result?.pages?.every((page) => page.status === 'READY');

              if (pagesReady) {
                record.updates.next({
                  fileId: record.fileId,
                  projectId: record.projectId,
                  codec: record.codec,
                  type: record.type,
                  file: record.file,
                  pages: result,
                  isSuccess: true,
                });

                this.stopMonitoringWithDelay(fileId);
              }
            })
          );
        }

      case TranscodeMonitorType.SingleFile:
        return this.projectFileService.getFileInfo(record.projectId, record.fileId).pipe(
          tap((file) => {
            record.file = file;

            const metadata = record.file?.transcodeMetadata?.find((c) => c?.codec);
            if (metadata) {
              switch (metadata.resolution) {
                case 'true':
                case 'false':
                  record.updates.next({
                    fileId: record.fileId,
                    projectId: record.projectId,
                    codec: record.codec,
                    type: record.type,
                    file,
                    isSuccess: metadata.resolution == 'true',
                  });
                  setTimeout(() => {
                    this.stopMonitoringFile(fileId);
                  }, this.stopInterval);
                  break;
                default:
                  break;
              }
            }
          })
        );

      case TranscodeMonitorType.SingleFileReplacement:
        return this.projectFileService.getFileInfo(record.projectId, record.replacementFileId).pipe(
          tap((file) => {
            record.replacementFile = file;
            if (file.malwareScanningStatus != MalwareScanningStatus.Unscanned) {
              record.updates.next({
                fileId: record.replacementFileId,
                projectId: record.projectId,
                codec: record.codec,
                type: record.type,
                file: null,
                isSuccess: file.malwareScanningStatus == MalwareScanningStatus.Clean,
              });
              this.stopMonitoringWithDelay(fileId);
            }
          })
        );

      case TranscodeMonitorType.PagesReplacement:
        if (!record?.file?.hasPage) {
          return this.projectFileService.getFileInfo(record.projectId, record.fileId).pipe(
            tap((file) => {
              record.file = file;

              const currentMetadata = file?.transcodeMetadata?.find((c) => c?.codec == record.codec);
              if (currentMetadata?.resolution == 'false') {
                record.updates.next({
                  fileId: record.fileId,
                  projectId: record.projectId,
                  codec: record.codec,
                  type: record.type,
                  file: record.file,
                  isSuccess: false,
                });
                this.stopMonitoringWithDelay(fileId);
              }
            })
          );
        } else {
          return this.subsectionService.getFilePages(record.fileId).pipe(
            tap((result) => {
              const pagesReady = result?.pages?.every((page) => page.status === 'READY');

              if (pagesReady) {
                record.updates.next({
                  fileId: record.fileId,
                  projectId: record.projectId,
                  codec: record.codec,
                  type: record.type,
                  file: record.file,
                  pages: result,
                  isSuccess: true,
                });
                this.setReplacementFileOpened(record.fileId);
                this.stopMonitoringWithDelay(fileId);
              }
            })
          );
        }
    }
  }

  private setupMonitoring() {
    this.hasFiles$
      .pipe(
        debounceTime(1000),
        switchMap((hasFiles) => {
          if (!hasFiles) {
            return [];
          }

          return timer(0, this.timerInterval).pipe(
            concatMap(() => {
              const fileIds = Array.from(this.fileIds);
              return fileIds.reduce((acc, fileId) => {
                return acc.pipe(
                  concatMap(() => this.pollFileTranscodeStatus(fileId)),
                  delay(this.delayBetweenRequests)
                );
              }, of(null));
            })
          );
        })
      )
      .subscribe();
  }

  private stopMonitoringWithDelay(fileId: string) {
    setTimeout(() => {
      this.stopMonitoringFile(fileId);
    }, this.stopInterval);
  }

  private isReplacementFileOpened(fileId: string) {
    try {
      const ids = JSON.parse(localStorage.getItem(this.replacementIdsKey)) || [];
      return ids.includes(fileId);
    } catch (error) {
      return false;
    }
  }

  private setReplacementFileOpened(fileId: string) {
    try {
      const ids = JSON.parse(localStorage.getItem(this.replacementIdsKey)) || [];
      if (!ids.includes(fileId)) {
        ids.push(fileId);
      }
      localStorage.setItem(this.replacementIdsKey, JSON.stringify(ids));
    } catch (error) {}
  }
}
